import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { User, Lock, Loader2, X, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";

export const LoginModal = ({ open, onClose, onSuccess }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setError("Ingresa usuario y contraseña.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { username: username.trim(), password });
      localStorage.setItem("access_token", data.access_token);
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={onClose}
          data-testid="login-modal-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ type: "spring", damping: 24, stiffness: 300 }}
            className="relative w-full max-w-sm rounded-2xl bg-white p-8 shadow-[0_20px_60px_rgb(0,0,0,0.15)]"
            onClick={(e) => e.stopPropagation()}
            data-testid="login-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Acceso administrativo"
          >
            <button
              onClick={onClose}
              aria-label="Cerrar"
              data-testid="login-modal-close-button"
              className="absolute right-4 top-4 rounded-lg p-1 text-[#64748B] transition-colors hover:bg-[#FBF6F3] hover:text-[#1E293B]"
            >
              <X size={18} />
            </button>
            <div className="mb-6 flex flex-col items-start gap-2">
              <div className="rounded-xl bg-[#1F2A5C]/10 p-2.5 text-[#1F2A5C]">
                <ShieldCheck size={22} strokeWidth={2} />
              </div>
              <h2 className="text-lg font-semibold text-[#1E293B]">Acceso administrativo</h2>
              <p className="text-sm text-[#64748B]">Ingresa tus credenciales para continuar.</p>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="relative">
                <User size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  type="text"
                  aria-label="Usuario"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Usuario"
                  data-testid="login-username-input"
                  className="w-full min-h-[48px] rounded-lg border border-[#E2E8F0] bg-white pl-10 pr-4 text-sm text-[#1E293B] outline-none transition-colors placeholder:text-[#94A3B8] focus:ring-2 focus:ring-[#C9848F] focus:border-transparent"
                />
              </div>
              <div className="relative">
                <Lock size={17} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  type="password"
                  aria-label="Contraseña"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña"
                  data-testid="login-password-input"
                  className="w-full min-h-[48px] rounded-lg border border-[#E2E8F0] bg-white pl-10 pr-4 text-sm text-[#1E293B] outline-none transition-colors placeholder:text-[#94A3B8] focus:ring-2 focus:ring-[#C9848F] focus:border-transparent"
                />
              </div>
              {error && (
                <p data-testid="login-error-message" className="text-sm text-red-500">
                  {error}
                </p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="login-submit-button"
                className="w-full min-h-[48px] rounded-xl bg-[#1F2A5C] text-sm font-semibold text-white shadow-md shadow-[#1F2A5C]/30 transition-transform transition-colors hover:bg-[#151E45] hover:-translate-y-[1px] active:scale-[0.98] disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {loading && <Loader2 size={17} className="animate-spin" />}
                Ingresar
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
